import React, { useCallback, useEffect, useId, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { m } from 'framer-motion';
import { FiAlertCircle, FiLoader, FiSearch, FiExternalLink, FiChevronLeft, FiChevronRight, FiImage, FiCode } from 'react-icons/fi';
import Header from './Header';
import Footer from './Footer';
import CookieBanner from './CookieBanner';
import { SITE_ORIGIN } from './siteConfig';
import { DEFAULT_PAGE_TITLE } from './seoDefaults';

const PATH = '/serp-overview';
const CANONICAL = `${SITE_ORIGIN}${PATH}`;

export default function SerpOverviewPage() {
  const queryFieldId = useId();
  const helperId = `${queryFieldId}-helper`;
  const statusId = `${queryFieldId}-status`;
  const resultsRef = useRef(null);

  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);
  const [page, setPage] = useState(1);

  useEffect(() => {
    document.title = 'SERP overview — see who ranks for any keyword | WebCodeCraft';
    const canonical = document.querySelector('link[rel="canonical"]');
    if (canonical) canonical.setAttribute('href', CANONICAL);
    const desc = document.querySelector('meta[name="description"]');
    const prevDesc = desc ? desc.getAttribute('content') : '';
    if (desc) desc.setAttribute('content', 'Free SERP overview: enter a keyword to see the top Google results, titles, snippets, Open Graph data and schema. No login. WebCodeCraft.');
    const ogTitle = document.querySelector('meta[property="og:title"]');
    const ogDesc = document.querySelector('meta[property="og:description"]');
    const ogUrl = document.querySelector('meta[property="og:url"]');
    const twTitle = document.querySelector('meta[property="twitter:title"]');
    const twDesc = document.querySelector('meta[property="twitter:description"]');
    const twUrl = document.querySelector('meta[property="twitter:url"]');
    const pageTitle = 'SERP overview — see who ranks for any keyword | WebCodeCraft';
    const pageDesc = 'Free SERP overview: enter a keyword to see the top Google results, titles, snippets, Open Graph data and schema. No login. WebCodeCraft.';
    if (ogTitle) ogTitle.setAttribute('content', pageTitle);
    if (ogDesc) ogDesc.setAttribute('content', pageDesc);
    if (ogUrl) ogUrl.setAttribute('content', CANONICAL);
    if (twTitle) twTitle.setAttribute('content', pageTitle);
    if (twDesc) twDesc.setAttribute('content', pageDesc);
    if (twUrl) twUrl.setAttribute('content', CANONICAL);
    const ld = document.createElement('script');
    ld.type = 'application/ld+json';
    ld.text = JSON.stringify({
      '@context': 'https://schema.org', '@type': 'WebPage',
      name: pageTitle, url: CANONICAL, description: pageDesc,
      isPartOf: { '@type': 'WebSite', name: 'WebCodeCraft', url: SITE_ORIGIN },
      publisher: { '@type': 'Organization', name: 'WebCodeCraft', url: SITE_ORIGIN }
    });
    ld.setAttribute('data-page', 'serp-overview');
    document.head.appendChild(ld);
    return () => {
      document.title = DEFAULT_PAGE_TITLE;
      if (canonical) canonical.setAttribute('href', `${SITE_ORIGIN}/`);
      if (desc && prevDesc != null) desc.setAttribute('content', prevDesc);
      ld.remove();
    };
  }, []);

  const fetchResults = useCallback(async (q, startPage) => {
    setError(null);
    setLoading(true);
    try {
      const offset = (startPage - 1) * 10;
      const res = await fetch('/api/serp-overview', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ query: q, offset })
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok) { setError(typeof json.error === 'string' ? json.error : 'Search failed.'); return; }
      setData(json);
      setPage(startPage);
      if (startPage > 1) requestAnimationFrame(() => { resultsRef.current?.focus(); });
    } catch { setError('Network error. Try again in a moment.'); }
    finally { setLoading(false); }
  }, []);

  const handleSubmit = useCallback((e) => {
    e.preventDefault();
    const q = query.trim();
    if (!q) { setError('Enter a search query.'); return; }
    setData(null);
    fetchResults(q, 1);
  }, [query, fetchResults]);

  const items = data?.items || [];

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 via-white to-slate-50">
      <Header />
      <CookieBanner />
      <main id="main-content" className="pt-20 min-h-screen min-w-0 overflow-x-clip">
        <section className="container mx-auto px-4 py-10 max-w-3xl" aria-labelledby="serp-heading">
          <m.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.45 }}>
            <p className="text-sm font-medium text-indigo-600 mb-2">
              <Link to="/check-your-seo" className="underline underline-offset-2 hover:text-indigo-800">Tools</Link>
              <span className="mx-1 text-slate-400" aria-hidden="true">/</span>
              <span className="text-slate-700">SERP overview</span>
            </p>
            <h1 id="serp-heading" className="text-3xl sm:text-4xl font-bold text-slate-900 font-display tracking-tight mb-4">
              SERP overview
            </h1>
            <p className="text-slate-600 leading-relaxed mb-6" id={helperId}>
              Enter a keyword to see who currently ranks on Google. Each result shows the title, snippet, Open Graph signals and structured data type at a glance.
            </p>
            <form onSubmit={handleSubmit} className="space-y-4" noValidate>
              <div>
                <label htmlFor={queryFieldId} className="block text-sm font-semibold text-slate-800 mb-1.5">Search query</label>
                <input id={queryFieldId} type="text" name="query" autoComplete="off"
                  placeholder="e.g. web design devon" value={query}
                  onChange={(ev) => setQuery(ev.target.value)}
                  className="form-input w-full max-w-xl" aria-describedby={helperId}
                  disabled={loading} maxLength={200} />
              </div>
              <m.button type="submit" className="btn btn-primary inline-flex items-center gap-2 disabled:opacity-60"
                disabled={loading} whileTap={{ scale: 0.98 }}>
                {loading
                  ? <><FiLoader className="animate-spin" aria-hidden="true" />Searching…</>
                  : <><FiSearch aria-hidden="true" />Search</>}
              </m.button>
            </form>
            <div id={statusId} role="status" aria-live="polite" className="mt-6 min-h-[1.25rem] text-sm">
              {error && (
                <p className="text-red-700 font-medium flex items-start gap-2">
                  <FiAlertCircle className="shrink-0 mt-0.5" aria-hidden="true" />{error}
                </p>
              )}
              {loading && <p className="text-slate-500">Searching the web…</p>}
            </div>
          </m.div>
        </section>

        {data?.ok && (
          <section ref={resultsRef} tabIndex={-1}
            className="container mx-auto px-4 pb-16 max-w-3xl outline-none space-y-4"
            aria-label="SERP results">
            <div className="flex items-center justify-between text-sm text-slate-500 mb-2">
              <span>About <strong className="text-slate-700">{data.totalResults.toLocaleString()}</strong> results ({data.searchTime.toFixed(2)}s)</span>
              <span className="text-xs text-slate-400">Brave Search</span>
            </div>

            {items.length === 0 && (
              <p className="text-slate-600 py-8 text-center">No results found for &ldquo;{data.query}&rdquo;.</p>
            )}

            <ol className="space-y-5">
              {items.map((item) => (
                <li key={item.position} className="bg-white rounded-xl border border-slate-200 shadow-sm p-4 sm:p-5 hover:border-slate-300 transition-colors">
                  <div className="flex items-start gap-3">
                    <span className="shrink-0 w-7 h-7 rounded-full bg-slate-100 flex items-center justify-center text-xs font-bold text-slate-600">
                      {item.position}
                    </span>
                    <div className="min-w-0 flex-1">
                      <a href={item.link} target="_blank" rel="noopener noreferrer"
                        className="text-indigo-700 font-semibold hover:underline underline-offset-2 leading-snug inline-flex items-center gap-1.5">
                        {item.title}
                        <FiExternalLink className="shrink-0 text-indigo-400" size={14} />
                      </a>
                      <p className="text-xs text-emerald-700 mt-0.5 truncate">{item.displayLink}</p>
                      <p className="text-sm text-slate-600 mt-1.5 leading-relaxed">{item.snippet}</p>
                      <div className="flex flex-wrap gap-2 mt-2">
                        {item.pagemap.hasOgImage && (
                          <span className="inline-flex items-center gap-1 text-xs bg-blue-50 text-blue-700 px-2 py-0.5 rounded-full border border-blue-200">
                            <FiImage size={11} /> og:image
                          </span>
                        )}
                        {item.pagemap.schema && (
                          <span className="inline-flex items-center gap-1 text-xs bg-purple-50 text-purple-700 px-2 py-0.5 rounded-full border border-purple-200">
                            <FiCode size={11} /> {item.pagemap.schema}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </li>
              ))}
            </ol>

            <div className="flex items-center justify-center gap-4 pt-4">
              <button type="button" disabled={page <= 1 || loading}
                onClick={() => fetchResults(data.query, page - 1)}
                className="inline-flex items-center gap-1 text-sm font-medium text-indigo-600 hover:text-indigo-800 disabled:text-slate-300 disabled:cursor-not-allowed">
                <FiChevronLeft size={16} /> Previous
              </button>
              <span className="text-sm text-slate-500">Page {page}</span>
              <button type="button" disabled={!data.hasNext || loading}
                onClick={() => fetchResults(data.query, page + 1)}
                className="inline-flex items-center gap-1 text-sm font-medium text-indigo-600 hover:text-indigo-800 disabled:text-slate-300 disabled:cursor-not-allowed">
                Next <FiChevronRight size={16} />
              </button>
            </div>
          </section>
        )}
        <Footer />
      </main>
    </div>
  );
}
